import { ArrowDownwardRounded, ArrowUpwardRounded, CloseRounded, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { fShortenNumber } from 'src/utils/format-number';
import { PinCandidate } from '../../perps-v1-shared/types';
import { errorMessage } from '../../perps-v1-shared/errorMessage';
import { PreviewRow, viewsApi } from '../dataProvider';
import { buildDisplayRows, displayRowKey } from './displayRows';

const PREVIEW_LIMIT = 50;
const PREVIEW_DEBOUNCE_MS = 600;

const SOURCE_COLOR: Record<PreviewRow['source'], 'success' | 'info' | 'default'> = {
  pinned: 'success',
  manual: 'info',
  rule: 'default',
};

const SOURCE_LABEL_KEY: Record<PreviewRow['source'], string> = {
  pinned: 'module.perpsV1Views.previewSourcePinned',
  manual: 'module.perpsV1Views.previewSourceManual',
  rule: 'module.perpsV1Views.previewSourceRule',
};

// changePercent24h -> "+19.42%" / "-3.10%", coloured like the sibling hot-symbols screens
// (src/pages/admin/resources/hot-symbols/components/shared.tsx ChangePercent: success/error,
// 2 decimals, always signed). Empty string covers the optimistic "—" placeholder row.
function formatChangePercent(raw: string): { text: string; color: 'success.main' | 'error.main' | 'text.secondary' } {
  const num = Number(raw);
  if (raw === '' || !Number.isFinite(num)) return { text: '—', color: 'text.secondary' };
  return {
    text: `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`,
    color: num >= 0 ? 'success.main' : 'error.main',
  };
}

// markPx -> up to 6 significant decimals, no trailing zeros, without collapsing small-priced
// symbols to 0. toPrecision(6) already reads as "6 significant figures" for the ranges perps
// mark prices live in; only fall back to a plain fixed-decimal render if that ever produces
// exponential notation (very large/tiny values outside the normal range).
function formatMarkPx(raw: string): string {
  const num = Number(raw);
  if (raw === '' || !Number.isFinite(num)) return '—';
  if (num === 0) return '0';
  let str = num.toPrecision(6);
  if (str.includes('e') || str.includes('E')) {
    str = num.toFixed(12);
  }
  if (str.includes('.')) {
    str = str.replace(/0+$/, '').replace(/\.$/, '');
  }
  return str;
}

// marketCap "0" means goback has not computed one for this symbol yet (see the contract note on
// AdminMarket.marketCap / MarketViewPreviewRow.marketCap in market_admin.gql) — render an em dash
// instead of literal "$0", which would read as "this market is worthless" rather than "not
// computed". "" is the same optimistic-placeholder sentinel volume24h/markPx use above for a row
// the confirming fetch hasn't landed for yet. Reuses fShortenNumber, the same helper the volume
// column formats with, rather than inventing a second number formatter.
function formatMarketCap(raw: string): string {
  if (raw === '' || raw === '0') return '—';
  return fShortenNumber(raw);
}

interface Props {
  // The SAME view-definition payload create/update would send (built by the dialog's shared
  // helper), plus whatever the caller wants appended (this component adds `limit` itself).
  body: Record<string, unknown>;
  // Stable primitive string that changes ONLY when a rule-relevant field changes (built by
  // ViewSymbolsDialog's previewTriggerKey). Drives the debounced auto-refresh below — it must stay
  // a primitive so the effect's dependency doesn't look "new" on every render and free-run.
  triggerKey: string;
  // Bumped by the dialog on discrete membership actions (add/remove/reorder) to force an
  // IMMEDIATE refetch that bypasses the debounce below — those actions must not leave a symbol
  // looking like it's in neither column while PREVIEW_DEBOUNCE_MS is still running. 0 is the
  // "never triggered" sentinel so this never double-fires the normal on-mount run.
  refreshNonce: number;
  disabled: boolean;
  // Translation key explaining why the refresh button is disabled.
  disabledReason: string;
  // form.includeSymbols with delisted entries already removed by the dialog (those render in
  // their own block above this panel) — anchors the manual block's order within the table and
  // drives the optimistic "just added" row + the up/down reorder bounds.
  manualSymbols: string[];
  // Candidate pool, used only to label/dex an optimistically-rendered manual row that hasn't
  // come back from the server yet.
  candidates: PinCandidate[];
  // Reports the full pre-exclude match set after every successful fetch, so the dialog can
  // compute the Available (include) and Exclude candidate pools. Never used to alter this
  // panel's own table, which always renders the post-exclude `data` the server returns.
  onResult: (result: { matchedSymbols: string[] }) => void;
  onRemoveManual: (symbol: string) => void;
  onMoveManual: (symbol: string, direction: -1 | 1) => void;
}

// The live preview doubles as the INCLUDE side's member list: "what would this rule actually
// show" AND "what's in the view, with per-row actions for manual entries" are the same table now
// — see decision D13. Auto-refreshes PREVIEW_DEBOUNCE_MS after the last rule-relevant edit (see
// `triggerKey`), immediately (bypassing the debounce) when `refreshNonce` changes, and still
// offers an explicit refresh button, since market data moves server-side too. A failed refresh
// notifies and keeps whatever the last successful result was on screen (stale-but-real beats no
// preview), and an in-flight refetch never blanks existing rows — only a thin progress bar
// appears — so the panel doesn't flicker on every keystroke's settled edit.
export default function ViewPreviewPanel({
  body,
  triggerKey,
  refreshNonce,
  disabled,
  disabledReason,
  manualSymbols,
  candidates,
  onResult,
  onRemoveManual,
  onMoveManual,
}: Props) {
  const translate = useTranslate();
  const notify = useNotify();

  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  // Always holds the CURRENT full payload, so a timer scheduled several renders ago (or the
  // manual button) sends what's on screen right now rather than a stale closure over `body`.
  const bodyRef = useRef(body);
  bodyRef.current = body;

  // Always holds the CURRENT onResult, same reasoning as bodyRef — avoids re-running the fetch
  // effects just because the parent re-created the callback.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  // Guards a slower earlier response from overwriting a newer one, and ignores any response that
  // lands after unmount.
  const requestSeq = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    // 必须在 setup 里重新置 true：StrictMode 下 React 会 setup -> cleanup -> setup，
    // 只在 cleanup 里置 false 会让组件挂载后永远被当成已卸载，导致所有请求结果被丢弃、
    // loading 永不结束。
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Pending debounce timer, shared with the manual refresh button so a click cancels it instead
  // of letting both fire.
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingDebounce = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  };

  const runPreview = async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    setLoading(true);
    try {
      const json = await viewsApi.preview({ ...bodyRef.current, limit: PREVIEW_LIMIT });
      if (!mountedRef.current || requestSeq.current !== seq) return;
      setRows(json?.data ?? []);
      setTotal(json?.total ?? 0);
      setHasRun(true);
      onResultRef.current({ matchedSymbols: json?.matchedSymbols ?? [] });
    } catch (error: any) {
      if (!mountedRef.current || requestSeq.current !== seq) return;
      notify(
        errorMessage(error, translate('module.perpsV1Views.previewError')),
        { type: 'error' }
      );
      // Deliberately no state change here — keep the last successful result on screen.
    } finally {
      if (mountedRef.current && requestSeq.current === seq) setLoading(false);
    }
  };

  // Auto-refresh: fires PREVIEW_DEBOUNCE_MS after `triggerKey` last changed. `triggerKey` only
  // changes for rule-relevant fields, so this effect does not re-run — and no request fires — for
  // edits to label/badge/parentCode/displayOrder/displayLimit. It also fires once on mount (the
  // normal first run of any effect) whenever code is already non-empty, which is what shows an
  // existing view's members as soon as the editor opens, without the admin clicking anything.
  useEffect(() => {
    if (disabled) return undefined;
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      runPreview();
    }, PREVIEW_DEBOUNCE_MS);
    return clearPendingDebounce;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerKey, disabled]);

  // Discrete add/remove/reorder actions bump `refreshNonce` to bypass the debounce above — a
  // symbol must not look like it's in neither column while PREVIEW_DEBOUNCE_MS is still running.
  // Runs AFTER the effect above in the same commit, so it cancels whatever debounce that effect
  // just (re)scheduled for the same triggerKey change and fires immediately instead. 0 is the
  // sentinel "never triggered" value so this never double-fires the initial on-mount run.
  useEffect(() => {
    if (refreshNonce === 0 || disabled) return;
    clearPendingDebounce();
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const handleManualRefresh = () => {
    clearPendingDebounce();
    runPreview();
  };

  // Merge the confirmed fetch with the in-progress draft so membership never looks wrong between
  // a discrete action and the refresh landing:
  //  - a manual row the admin just removed disappears immediately, even though the last fetch
  //    still has it;
  //  - a manual row the admin just added renders right away with "—" metric cells, at the end of
  //    the manual block, even though the fetch hasn't confirmed it yet;
  //  - the manual block's order always matches `manualSymbols` (the live draft), so a reorder is
  //    reflected instantly rather than waiting on the network.
  // Pinned/rule rows are never touched here — they aren't editable from this panel.
  const candidateMap = useMemo(() => new Map(candidates.map((c) => [c.symbol, c])), [candidates]);
  const displayRows = useMemo(
    () => buildDisplayRows(rows, manualSymbols, candidateMap),
    [rows, manualSymbols, candidateMap]
  );

  const refreshButton = (
    <LoadingButton
      variant="outlined"
      size="small"
      onClick={handleManualRefresh}
      loading={loading}
      disabled={disabled}
    >
      {translate('module.perpsV1Views.previewRefresh')}
    </LoadingButton>
  );

  const renderAction = (row: PreviewRow) => {
    if (row.source === 'manual') {
      const index = manualSymbols.indexOf(row.symbol);
      const count = manualSymbols.length;
      return (
        <Stack direction="row" spacing={0} justifyContent="flex-end">
          <IconButton
            size="small"
            disabled={index <= 0}
            onClick={() => onMoveManual(row.symbol, -1)}
            aria-label={translate('module.perpsV1Views.membersMoveUp')}
          >
            <ArrowUpwardRounded fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={index < 0 || index >= count - 1}
            onClick={() => onMoveManual(row.symbol, 1)}
            aria-label={translate('module.perpsV1Views.membersMoveDown')}
          >
            <ArrowDownwardRounded fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onRemoveManual(row.symbol)}
            aria-label={translate('module.perpsV1Views.membersRemove')}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      );
    }
    if (row.source === 'rule') {
      return (
        <Stack direction="row" justifyContent="flex-end">
          <Tooltip title={translate('module.perpsV1Views.membersRuleActionHint')}>
            <InfoOutlined fontSize="small" sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ height: 2 }}>{loading ? <LinearProgress sx={{ height: 2 }} /> : null}</Box>

      <Box>
        {disabled ? (
          <Tooltip title={translate(disabledReason)}>
            <span>{refreshButton}</span>
          </Tooltip>
        ) : (
          refreshButton
        )}
      </Box>

      {hasRun ? (
        <Typography variant="body2" color="text.secondary">
          {translate('module.perpsV1Views.previewShowing', { shown: rows.length, total })}
        </Typography>
      ) : null}

      {hasRun && displayRows.length === 0 ? (
        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {translate('module.perpsV1Views.previewEmpty')}
          </Typography>
        </Box>
      ) : null}

      {displayRows.length > 0 ? (
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>{translate('module.perpsV1Views.previewColumnSymbol')}</TableCell>
                <TableCell>{translate('module.perpsV1Views.previewColumnSource')}</TableCell>
                <TableCell align="right">{translate('module.perpsV1Views.previewColumnMarkPx')}</TableCell>
                <TableCell align="right">{translate('module.perpsV1Views.previewColumnChange')}</TableCell>
                <TableCell align="right">{translate('module.perpsV1Views.previewColumnVolume')}</TableCell>
                <TableCell align="right">{translate('module.perpsV1Views.previewColumnMarketCap')}</TableCell>
                <TableCell align="right" sx={{ width: 132 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, index) => {
                const change = formatChangePercent(row.changePercent24h);
                return (
                  <TableRow key={displayRowKey(row)} hover>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" fontFamily="monospace">
                          {row.displaySymbol}
                        </Typography>
                        {row.dex ? <Chip size="small" variant="outlined" label={row.dex} /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {/* 契约只返回 pinned/manual/rule；万一出现未知值，原样展示该值，
                          而不是 translate(undefined) 渲染成 "undefined" */}
                      <Chip
                        size="small"
                        color={SOURCE_COLOR[row.source] ?? 'default'}
                        label={
                          SOURCE_LABEL_KEY[row.source]
                            ? translate(SOURCE_LABEL_KEY[row.source])
                            : row.source
                        }
                      />
                    </TableCell>
                    <TableCell align="right">{formatMarkPx(row.markPx)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: change.color }}>
                        {change.text}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {row.volume24h === '' ? '—' : fShortenNumber(row.volume24h)}
                    </TableCell>
                    <TableCell align="right">{formatMarketCap(row.marketCap)}</TableCell>
                    <TableCell align="right">{renderAction(row)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Stack>
  );
}
